// FILE: src/api/adminSupportApi.js
import { adminApi, adminJson, adminText } from "./adminHttp";

/* =========================================================
   Small helpers (STRICT + CONSISTENT)
   ========================================================= */

function enc(v) {
  return encodeURIComponent(String(v));
}

function requireId(name, value) {
  if (value === undefined || value === null || String(value).trim() === "") {
    throw new Error(`${name} is required`);
  }
}

function safeStr(v) {
  return v === undefined || v === null ? "" : String(v);
}

function cleanStr(v) {
  const s = safeStr(v).trim();
  return s ? s : "";
}

/**
 * Backend sometimes uses an envelope: { ok, message, ts, data }.
 * Keep compatibility across ALL endpoints (list + detail + actions).
 */
function unwrapApi(x) {
  if (x && typeof x === "object" && "ok" in x && "data" in x) return x.data;
  return x;
}

/**
 * Build query params:
 * - skips undefined/null/empty-string
 * - trims strings
 * - converts numbers/booleans to string
 */
function buildParams(obj = {}) {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = String(v).trim();
    if (!s) return;
    params.set(k, s);
  });
  return params;
}

/** Common filter helpers */
function asOptionalAll(v) {
  const s = cleanStr(v);
  if (!s) return undefined;
  if (s.toUpperCase() === "ALL") return undefined;
  return s;
}

function asOptionalQuery(v) {
  const s = cleanStr(v);
  return s ? s : undefined;
}

/* --------------------- request helpers --------------------- */

function postJson(path, payload) {
  const p = payload ?? {};
  return adminJson(path, {
    method: "POST",
    json: p,
    body: p,
  });
}

function patchJson(path, payload) {
  const p = payload ?? {};
  return adminJson(path, {
    method: "PATCH",
    json: p,
    body: p,
  });
}

function postNoBody(path) {
  return adminJson(path, { method: "POST" });
}

function delNoBody(path) {
  return adminJson(path, { method: "DELETE" });
}

/* =========================================================
   USERS
   ========================================================= */

export function searchSupportUsers(q, page = 0, size = 20) {
  const params = buildParams({ q: cleanStr(q), page, size });
  return adminJson(`/admin/support/users/search?${params.toString()}`);
}

export async function getSupportUserDetail(userId) {
  requireId("userId", userId);
  return unwrapApi(await adminJson(`/admin/support/users/${enc(userId)}`));
}

/* =========================================================
   REQUESTS (LIST + DETAIL)
   ========================================================= */

export async function searchAdminSupportRequests({
  q,
  status,
  paymentStatus,
  page = 0,
  size = 50,
} = {}) {
  const params = buildParams({
    q: asOptionalQuery(q),
    status: asOptionalAll(status),
    paymentStatus: asOptionalAll(paymentStatus),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/requests?${params.toString()}`));
}

export async function searchAdminSupportRequestsForUser({
  userId,
  q,
  status,
  paymentStatus,
  page = 0,
  size = 50,
} = {}) {
  requireId("userId", userId);

  const params = buildParams({
    q: asOptionalQuery(q),
    status: asOptionalAll(status),
    paymentStatus: asOptionalAll(paymentStatus),
    page,
    size,
  });

  return unwrapApi(
    await adminJson(`/admin/support/requests/by-user/${enc(userId)}?${params.toString()}`)
  );
}

export async function getAdminSupportRequestDetail(requestId) {
  requireId("requestId", requestId);
  return unwrapApi(await adminJson(`/admin/support/requests/${enc(requestId)}`));
}

/* -------------------- REQUESTS (ACTIONS: text/plain body) -------------------- */

export function adminCancelRequest(requestId, reason) {
  requireId("requestId", requestId);
  return adminText(`/admin/support/requests/${enc(requestId)}/cancel`, safeStr(reason));
}

export function adminReopenRequest(requestId, note) {
  requireId("requestId", requestId);
  return adminText(`/admin/support/requests/${enc(requestId)}/reopen`, safeStr(note));
}

export function adminRerunOffers(requestId, note) {
  requireId("requestId", requestId);
  return adminText(`/admin/support/requests/${enc(requestId)}/rerun`, safeStr(note));
}

export function adminAdvanceBatch(requestId, note) {
  requireId("requestId", requestId);
  return adminText(`/admin/support/requests/${enc(requestId)}/advance`, safeStr(note));
}

/* =========================================================
   BOOKINGS (ADMIN SUPPORT)
   ========================================================= */

export async function searchAdminSupportBookings({
  q,
  status,
  rideStatus,
  page = 0,
  size = 50,
} = {}) {
  const params = buildParams({
    q: asOptionalQuery(q),
    status: asOptionalAll(status),
    rideStatus: asOptionalAll(rideStatus),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/bookings?${params.toString()}`));
}

export async function searchAdminSupportBookingsForUser({
  userId,
  q,
  status,
  rideStatus,
  page = 0,
  size = 30,
} = {}) {
  requireId("userId", userId);

  const params = buildParams({
    q: asOptionalQuery(q),
    status: asOptionalAll(status),
    rideStatus: asOptionalAll(rideStatus),
    page,
    size,
  });

  return unwrapApi(
    await adminJson(`/admin/support/bookings/by-user/${enc(userId)}?${params.toString()}`)
  );
}

export async function getAdminSupportBookingDetail(
  bookingId,
  { auditPage = 0, auditSize = 50 } = {}
) {
  requireId("bookingId", bookingId);

  const params = buildParams({ auditPage, auditSize });
  return unwrapApi(
    await adminJson(`/admin/support/bookings/${enc(bookingId)}?${params.toString()}`)
  );
}

/* -------------------- BOOKINGS (ACTIONS: JSON body) -------------------- */

export function adminBookingOwnerAccept(bookingId) {
  requireId("bookingId", bookingId);
  return postNoBody(`/admin/support/bookings/${enc(bookingId)}/actions/owner-accept`);
}

export function adminBookingAssignDriver(bookingId, { driverId, reason } = {}) {
  requireId("bookingId", bookingId);
  requireId("driverId", driverId);

  return postJson(`/admin/support/bookings/${enc(bookingId)}/actions/assign-driver`, {
    driverId: Number(driverId),
    reason: safeStr(reason),
  });
}

export function adminBookingDriverAccept(bookingId) {
  requireId("bookingId", bookingId);
  return postNoBody(`/admin/support/bookings/${enc(bookingId)}/actions/driver-accept`);
}

export function adminBookingDriverDecline(bookingId, { reason } = {}) {
  requireId("bookingId", bookingId);
  const r = cleanStr(reason);
  if (!r) throw new Error("reason is required");

  return postJson(`/admin/support/bookings/${enc(bookingId)}/actions/driver-decline`, { reason: r });
}

export function adminBookingCancelReassign(bookingId, { reason } = {}) {
  requireId("bookingId", bookingId);
  const r = cleanStr(reason);
  if (!r) throw new Error("reason is required");

  return postJson(`/admin/support/bookings/${enc(bookingId)}/actions/cancel-reassign`, { reason: r });
}

export function adminBookingCancelBooking(bookingId, { reason } = {}) {
  requireId("bookingId", bookingId);
  const r = cleanStr(reason);
  if (!r) throw new Error("reason is required");

  return postJson(`/admin/support/bookings/${enc(bookingId)}/actions/cancel-booking`, { reason: r });
}

/* =========================================================
   PAYMENTS / REFUNDS (ADMIN SUPPORT)
   ========================================================= */

export async function listAdminSupportPaymentsForUser({
  userId,
  q,
  status,
  page = 0,
  size = 25,
} = {}) {
  requireId("userId", userId);

  const params = buildParams({
    q: asOptionalQuery(q),
    status: asOptionalAll(status),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/users/${enc(userId)}/payments?${params.toString()}`));
}

export async function getAdminSupportPaymentDetail(paymentId) {
  requireId("paymentId", paymentId);
  return unwrapApi(await adminJson(`/admin/support/payments/${enc(paymentId)}`));
}

/* -------------------- Refund helpers -------------------- */

export async function adminSupportRefundPreviewForBooking(bookingId) {
  requireId("bookingId", bookingId);
  return unwrapApi(await adminJson(`/admin/support/bookings/${enc(bookingId)}/refund-preview`, { method: "GET" }));
}

export async function adminSupportIssueRefundForBooking(bookingId, { reason, amount } = {}) {
  requireId("bookingId", bookingId);
  const r = cleanStr(reason);
  if (!r) throw new Error("reason is required");

  return unwrapApi(
    await adminJson(`/admin/support/bookings/${enc(bookingId)}/refunds`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: r, amount: amount ?? null }),
    })
  );
}

export async function adminSupportRefreshRefundStatusForBooking(bookingId) {
  requireId("bookingId", bookingId);
  return unwrapApi(
    await adminJson(`/admin/support/bookings/${enc(bookingId)}/refunds/refresh`, { method: "POST" })
  );
}

export async function adminSupportRefundPreviewForRequest(requestId) {
  requireId("requestId", requestId);
  return unwrapApi(await adminJson(`/admin/support/requests/${enc(requestId)}/refund-preview`, { method: "GET" }));
}

export async function adminSupportIssueRefundForRequest(requestId, { reason, amount } = {}) {
  requireId("requestId", requestId);
  const r = cleanStr(reason);
  if (!r) throw new Error("reason is required");

  return unwrapApi(
    await adminJson(`/admin/support/requests/${enc(requestId)}/refunds`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: r, amount: amount ?? null }),
    })
  );
}

export async function adminSupportRefreshRefundStatusForRequest(requestId) {
  requireId("requestId", requestId);
  return unwrapApi(
    await adminJson(`/admin/support/requests/${enc(requestId)}/refunds/refresh`, { method: "POST" })
  );
}

export async function adminBookingActionThenFetchDetail(bookingId, actionPromise) {
  requireId("bookingId", bookingId);
  await actionPromise;
  return getAdminSupportBookingDetail(bookingId);
}

/* =========================================================
   NOTES (generic support notes)
   ========================================================= */

export async function listAdminSupportNotes({
  entityType,
  entityId,
  q,
  category,
  page = 0,
  size = 25,
} = {}) {
  requireId("entityType", entityType);
  requireId("entityId", entityId);

  const params = buildParams({
    q: asOptionalQuery(q),
    category: asOptionalAll(category), // if you use ALL in UI for category
    page,
    size,
  });

  return unwrapApi(
    await adminJson(`/admin/support/${enc(entityType)}/${enc(entityId)}/notes?${params.toString()}`)
  );
}

export async function createAdminSupportNote({
  entityType,
  entityId,
  text,
  category,
  pinned = false,
} = {}) {
  requireId("entityType", entityType);
  requireId("entityId", entityId);
  requireId("text", text);

  return unwrapApi(
    await postJson(`/admin/support/${enc(entityType)}/${enc(entityId)}/notes`, {
      text: cleanStr(text),
      category: cleanStr(category) || null,
      pinned: !!pinned,
    })
  );
}

export async function toggleAdminSupportNotePin(noteId, { pinned } = {}) {
  requireId("noteId", noteId);
  return unwrapApi(
    await adminJson(
      `/admin/support/notes/${enc(noteId)}/pin?pinned=${encodeURIComponent(String(!!pinned))}`,
      { method: "POST" }
    )
  );
}

export async function deleteAdminSupportNote(noteId) {
  requireId("noteId", noteId);
  return unwrapApi(await delNoBody(`/admin/support/notes/${enc(noteId)}`));
}

/* =========================================================
   HELP DESK (ADMIN SUPPORT)
   ========================================================= */

export async function adminListHelpMessages({
  actorType,
  status,
  requirementsOnly,
  q,
  page = 0,
  size = 30,
} = {}) {
  const params = buildParams({
    actorType: asOptionalAll(actorType),
    status: asOptionalAll(status),
    requirementsOnly: requirementsOnly ? "true" : undefined,
    q: asOptionalQuery(q),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/help?${params.toString()}`));
}

function filenameFromContentDisposition(cd) {
  if (!cd) return "";
  const mStar = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (mStar && mStar[1]) {
    try {
      return decodeURIComponent(mStar[1].replace(/"/g, "").trim());
    } catch {
      return mStar[1].replace(/"/g, "").trim();
    }
  }
  const m = cd.match(/filename\s*=\s*"([^"]+)"/i) || cd.match(/filename\s*=\s*([^;]+)/i);
  return m && m[1] ? String(m[1]).replace(/"/g, "").trim() : "";
}

export async function adminExportHelpMessages({
  actorType,
  status,
  requirementsOnly,
  q,
} = {}) {
  const params = buildParams({
    actorType: asOptionalAll(actorType),
    status: asOptionalAll(status),
    requirementsOnly: requirementsOnly ? "true" : undefined,
    q: asOptionalQuery(q),
  });

  const res = await adminApi(`/admin/support/help/export?${params.toString()}`, {
    method: "GET",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Export failed (${res.status})`);
  }

  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const filename = filenameFromContentDisposition(cd) || "support-help-export.csv";
  return { blob, filename };
}

export async function adminUpdateHelpMessageStatus(
  helpMessageId,
  { status, adminResolutionNote } = {}
) {
  requireId("helpMessageId", helpMessageId);
  return unwrapApi(
    await patchJson(`/admin/support/help/${enc(helpMessageId)}/status`, {
      status: cleanStr(status) || "PENDING",
      adminResolutionNote: cleanStr(adminResolutionNote) || null,
    })
  );
}

/* =========================================================
   OWNERS (ADMIN SUPPORT)
   ========================================================= */

export function searchSupportOwners(q, page = 0, size = 20) {
  const params = buildParams({ q: cleanStr(q), page, size });
  return adminJson(`/admin/support/owners/search?${params.toString()}`);
}

export async function getSupportOwnerDetail(ownerId) {
  requireId("ownerId", ownerId);
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}`));
}

export async function pushSupportOwnerLateCancelPenalty(
  ownerId,
  { bookingId = null, planKey = "", amount = null, note = "" } = {}
) {
  requireId("ownerId", ownerId);
  return unwrapApi(
    await postJson(`/admin/support/owners/${enc(ownerId)}/late-cancel/push`, {
      bookingId,
      planKey: cleanStr(planKey) || null,
      amount,
      note: cleanStr(note) || null,
    })
  );
}

export async function enableSupportOwnerLateCancelBlock(ownerId, { note = "" } = {}) {
  requireId("ownerId", ownerId);
  return unwrapApi(
    await postJson(`/admin/support/owners/${enc(ownerId)}/late-cancel/enable`, {
      note: cleanStr(note) || null,
    })
  );
}

export async function listSupportOwnerVehicles({ ownerId, page = 0, size = 30 } = {}) {
  requireId("ownerId", ownerId);
  const params = buildParams({ page, size });
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/vehicles?${params.toString()}`));
}

export async function listSupportOwnerInbox({ ownerId, status, q, page = 0, size = 30 } = {}) {
  requireId("ownerId", ownerId);

  const params = buildParams({
    status: asOptionalAll(status),
    q: asOptionalQuery(q),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/inbox?${params.toString()}`));
}

export async function listSupportOwnerNotes({ ownerId, page = 0, size = 30 } = {}) {
  requireId("ownerId", ownerId);
  const params = buildParams({ page, size });
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/notes?${params.toString()}`));
}

export async function createSupportOwnerNote({ ownerId, note } = {}) {
  requireId("ownerId", ownerId);
  const n = cleanStr(note);
  if (!n) throw new Error("note is required");

  return unwrapApi(
    await adminJson(`/admin/support/owners/${enc(ownerId)}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: n }),
    })
  );
}

/* ✅ OWNER BOOKINGS */

export async function listSupportOwnerBookings(ownerId, { status, q, page = 0, size = 30 } = {}) {
  requireId("ownerId", ownerId);

  const params = buildParams({
    status: asOptionalAll(status),
    q: asOptionalQuery(q),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/bookings?${params.toString()}`));
}

export async function getSupportOwnerBookingDetail(ownerId, bookingId) {
  requireId("ownerId", ownerId);
  requireId("bookingId", bookingId);
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/bookings/${enc(bookingId)}`));
}

/* ✅ OWNER RIDES */

export async function listSupportOwnerRides({ ownerId, q, status, page = 0, size = 30 } = {}) {
  requireId("ownerId", ownerId);

  const params = buildParams({
    q: asOptionalQuery(q),
    status: asOptionalAll(status),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/rides?${params.toString()}`));
}

export async function getSupportOwnerRideDetail(ownerId, rideId) {
  requireId("ownerId", ownerId);
  requireId("rideId", rideId);
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/rides/${enc(rideId)}`));
}

/* =========================================================
   ✅ OWNER PAYOUTS (BACKEND-ALIGNED)
   ========================================================= */

export async function getSupportOwnerPayoutOverview({ ownerId, pendingPage = 0, pendingSize = 20 } = {}) {
  requireId("ownerId", ownerId);
  const params = buildParams({ pendingPage, pendingSize });
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/payouts/overview?${params.toString()}`));
}

export async function getSupportOwnerPayoutAccount({ ownerId } = {}) {
  requireId("ownerId", ownerId);
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/payouts/account`));
}

export async function listSupportOwnerPendingPayoutBookings({ ownerId, page = 0, size = 30 } = {}) {
  requireId("ownerId", ownerId);
  const params = buildParams({ page, size });
  return unwrapApi(
    await adminJson(`/admin/support/owners/${enc(ownerId)}/payouts/pending-bookings?${params.toString()}`)
  );
}

export async function listSupportOwnerPayouts({ ownerId, status, q, page = 0, size = 30 } = {}) {
  requireId("ownerId", ownerId);

  const params = buildParams({
    status: asOptionalAll(status),
    q: asOptionalQuery(q),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/payouts?${params.toString()}`));
}

export async function getSupportOwnerPayoutTxnDetail({ ownerId, txnId } = {}) {
  requireId("ownerId", ownerId);
  requireId("txnId", txnId);
  return unwrapApi(await adminJson(`/admin/support/owners/${enc(ownerId)}/payouts/txns/${enc(txnId)}`));
}

/* -------------------- Backward compatible aliases (keep old imports working) -------------------- */

export function listSupportOwnerMatchOffers(ownerId) {
  return listSupportOwnerInbox({ ownerId, page: 0, size: 30 });
}

export function listSupportOwnerPendingPayouts(ownerId) {
  return listSupportOwnerPayouts({ ownerId, page: 0, size: 30 });
}

/* =========================================================
   DRIVERS (ADMIN SUPPORT)
   ========================================================= */

export function searchSupportDrivers(q, page = 0, size = 20) {
  const params = buildParams({ q: cleanStr(q), page, size });
  return adminJson(`/admin/support/drivers/search?${params.toString()}`);
}

export async function getSupportDriverDetail(driverId) {
  requireId("driverId", driverId);
  return unwrapApi(await adminJson(`/admin/support/drivers/${enc(driverId)}`));
}

/**
 * ✅ FIXED: do NOT send status=ALL (this was the driver-only bug)
 * Also trims q and omits it if empty.
 */
export async function listSupportDriverBookings(
  driverId,
  { status, q, page = 0, size = 30 } = {}
) {
  requireId("driverId", driverId);

  const params = buildParams({
    status: asOptionalAll(status),
    q: asOptionalQuery(q),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/drivers/${enc(driverId)}/bookings?${params.toString()}`));
}

export async function getSupportDriverBookingDetail(driverId, bookingId) {
  requireId("driverId", driverId);
  requireId("bookingId", bookingId);
  return unwrapApi(
    await adminJson(`/admin/support/drivers/${enc(driverId)}/bookings/${enc(bookingId)}`)
  );
}

/**
 * ✅ FIXED: do NOT send status=ALL (this was the driver-only bug)
 */
export async function listSupportDriverRides(
  driverId,
  { status, q, page = 0, size = 30 } = {}
) {
  requireId("driverId", driverId);

  const params = buildParams({
    status: asOptionalAll(status),
    q: asOptionalQuery(q),
    page,
    size,
  });

  return unwrapApi(await adminJson(`/admin/support/drivers/${enc(driverId)}/rides?${params.toString()}`));
}

export async function getSupportDriverRideDetail(driverId, rideId) {
  requireId("driverId", driverId);
  requireId("rideId", rideId);
  return unwrapApi(await adminJson(`/admin/support/drivers/${enc(driverId)}/rides/${enc(rideId)}`));
}

export async function listSupportDriverNotes(driverId, { page = 0, size = 30 } = {}) {
  requireId("driverId", driverId);
  const params = buildParams({ page, size });
  return unwrapApi(await adminJson(`/admin/support/drivers/${enc(driverId)}/notes?${params.toString()}`));
}

export async function createSupportDriverNote(driverId, note) {
  requireId("driverId", driverId);
  const n = cleanStr(note);
  if (!n) throw new Error("note is required");
  return unwrapApi(await postJson(`/admin/support/drivers/${enc(driverId)}/notes`, { note: n }));
}

/* =========================================================
   (Optional) Exports you might be using elsewhere
   ========================================================= */

// You already had patchJson defined; keep exported only if used by other modules
export { patchJson };
